import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import HomePage from "./pages/HomePage";
import EditorPage from "./pages/EditorPage";
import TemplatesPage from "./pages/TemplatesPage";
import TemplatePreviewPage from "./pages/TemplatePreviewPage";
import DesignViewerPage from "./pages/DesignViewerPage";
import PricingPage from "./pages/PricingPage";
import BlogPage from "./pages/BlogPage";
import ContactPage from "./pages/ContactPage";
import LoginPage from "./pages/LoginPage";
import NotFoundPage from "./pages/NotFoundPage";
import AboutPage from "./pages/AboutPage";
import PolicyPage from "./pages/PolicyPage";
import AdminContactMessages from "./pages/admin/AdminContactMessages";
import { PRIVACY, TERMS, COOKIES } from "./content/sitePages";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminCategories from "./pages/admin/AdminCategories";
import AdminElements from "./pages/admin/AdminElements";
import AdminTemplates from "./pages/admin/AdminTemplates";
import AdminTemplateImport from "./pages/admin/AdminTemplateImport";
import AdminBlog from "./pages/admin/AdminBlog";
import AdminAssets from "./pages/admin/AdminAssets";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminSubmissions from "./pages/admin/AdminSubmissions";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import AdminDesigners from "./pages/admin/AdminDesigners";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminCollections from "./pages/admin/AdminCollections";
import AdminBlogCategories from "./pages/admin/AdminBlogCategories";
import AdminApprovals from "./pages/admin/AdminApprovals";
import AdminTemplateCategories from "./pages/admin/AdminTemplateCategories";
import AdminTemplateGenerator from "./pages/admin/AdminTemplateGenerator";
import AdminAITemplates from "./pages/admin/AdminAITemplates";
import AdminAIDesignEngine from "./pages/admin/AdminAIDesignEngine";
import AdminAITemplateFactory from "./pages/admin/AdminAITemplateFactory";
import AdminAICreativeDirector from "./pages/admin/AdminAICreativeDirector";
import AdminAIPlayground from "./pages/admin/AdminAIPlayground";
import AITemplateGeneratorPage from "./aiTemplateGenerator/pages/AITemplateGeneratorPage";
import AdminTemplateLibrary from "./pages/admin/AdminTemplateLibrary";
import AdminTextRepair from "./pages/admin/AdminTextRepair";
import AdminMarketplacePayouts from "./pages/admin/AdminMarketplacePayouts";
import AdminCreators from "./pages/admin/AdminCreators";
import AdminCreatorDetail from "./pages/admin/AdminCreatorDetail";
import AdminReports from "./pages/admin/AdminReports";
import DesignerPage from "./pages/DesignerPage";
import DesignerLogin from "./pages/designer/DesignerLogin";
import DesignerDashboard from "./pages/designer/DesignerDashboard";
import DesignerMyTemplates from "./pages/designer/DesignerMyTemplates";
import DesignerSubmissions from "./pages/designer/DesignerSubmissions";
import DesignerAITemplates from "./pages/designer/DesignerAITemplates";
import DesignerProfile from "./pages/designer/DesignerProfile";
import DesignerNotifications from "./pages/designer/DesignerNotifications";
import DesignerCreatePage from "./pages/designer/DesignerCreatePage";
import UserDashboard from "./pages/UserDashboard";
import UserMyDesigns from "./pages/UserMyDesigns";
import UserSettings from "./pages/UserSettings";
import UserSaved from "./pages/UserSaved";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import PaymentTemplateSuccess from "./pages/PaymentTemplateSuccess";
import MarketplacePage from "./pages/MarketplacePage";
import MarketplaceBrowsePage from "./pages/MarketplaceBrowsePage";
import MarketplaceTemplatePage from "./pages/MarketplaceTemplatePage";
import MarketplaceCreatorPage from "./pages/MarketplaceCreatorPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import InvitationPage from "./pages/InvitationPage";
import NotificationsPage from "./pages/NotificationsPage";
import SharedWithMePage from "./pages/SharedWithMePage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import SvgCompatibilityTest from "./pages/SvgCompatibilityTest";
import CreatorBecome from "./pages/creator/CreatorBecome";
import CreatorOnboardingProfile from "./pages/creator/CreatorOnboardingProfile";
import CreatorOnboardingFirstTemplate from "./pages/creator/CreatorOnboardingFirstTemplate";
import CreatorDashboard from "./pages/creator/CreatorDashboard";
import CreatorTemplates from "./pages/creator/CreatorTemplates";
import CreatorReviews from "./pages/creator/CreatorReviews";
import CreatorSales from "./pages/creator/CreatorSales";
import CreatorEarnings from "./pages/creator/CreatorEarnings";
import CreatorPayouts from "./pages/creator/CreatorPayouts";
import CreatorAnalytics from "./pages/creator/CreatorAnalytics";
import CreatorStore from "./pages/creator/CreatorStore";
import CreatorProfile from "./pages/creator/CreatorProfile";
import CreatorNotifications from "./pages/creator/CreatorNotifications";
import CreatorCreate from "./pages/creator/CreatorCreate";
import CreatorVerification from "./pages/creator/CreatorVerification";
import { Toaster } from "./components/ui/toaster";

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/editor" element={<EditorPage />} />
          <Route path="/editor/:id" element={<EditorPage />} />
          <Route path="/templates" element={<TemplatesPage />} />
          <Route path="/template/:templateId" element={<TemplatePreviewPage />} />
          <Route path="/design/:shareToken" element={<DesignViewerPage />} />
          <Route path="/invitation/:token" element={<InvitationPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/shared-with-me" element={<SharedWithMePage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/privacy" element={<PolicyPage doc={PRIVACY} />} />
          <Route path="/terms" element={<PolicyPage doc={TERMS} />} />
          <Route path="/cookies" element={<PolicyPage doc={COOKIES} />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:id" element={<BlogPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Developer / QA — SVG fidelity comparison page */}
          <Route path="/dev/svg-compat" element={<SvgCompatibilityTest />} />

          {/* User Routes */}
          <Route path="/dashboard" element={<UserDashboard />} />
          <Route path="/my-designs" element={<UserMyDesigns />} />
          <Route path="/settings" element={<UserSettings />} />
          <Route path="/saved" element={<UserSaved />} />

          {/* Marketplace Routes */}
          <Route path="/marketplace" element={<MarketplacePage />} />
          <Route path="/marketplace/browse" element={<MarketplaceBrowsePage />} />
          <Route path="/marketplace/templates/:templateId" element={<MarketplaceTemplatePage />} />
          <Route path="/marketplace/creators/:creatorId" element={<MarketplaceCreatorPage />} />

          {/* Payment Routes */}
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/payment/template/success" element={<PaymentTemplateSuccess />} />

          {/* Creator Hub — public pitch */}
          <Route path="/creator/become" element={<CreatorBecome />} />
          <Route path="/creator/login" element={<HomePage />} />

          {/* Creator Hub — onboarding */}
          <Route path="/creator/onboarding/profile" element={<CreatorOnboardingProfile />} />
          <Route path="/creator/onboarding/first-template" element={<CreatorOnboardingFirstTemplate />} />

          {/* Creator Hub — dashboard */}
          <Route path="/creator/dashboard" element={<CreatorDashboard />} />
          <Route path="/creator/create" element={<CreatorCreate />} />
          <Route path="/creator/templates" element={<CreatorTemplates />} />
          <Route path="/creator/reviews" element={<CreatorReviews />} />
          <Route path="/creator/sales" element={<CreatorSales />} />
          <Route path="/creator/earnings" element={<CreatorEarnings />} />
          <Route path="/creator/payouts" element={<CreatorPayouts />} />
          <Route path="/creator/analytics" element={<CreatorAnalytics />} />
          <Route path="/creator/store" element={<CreatorStore />} />
          <Route path="/creator/profile" element={<CreatorProfile />} />
          <Route path="/creator/notifications" element={<CreatorNotifications />} />
          <Route path="/creator/verification" element={<CreatorVerification />} />

          {/* Legacy Designer Page */}
          <Route path="/designer" element={<DesignerPage />} />

          {/* Designer Portal */}
          <Route path="/designer/login" element={<DesignerLogin />} />
          <Route path="/designer/dashboard" element={<DesignerDashboard />} />
          <Route path="/designer/my-templates" element={<DesignerMyTemplates />} />
          <Route path="/designer/submissions" element={<DesignerSubmissions />} />
          <Route path="/designer/ai-templates" element={<DesignerAITemplates />} />
          <Route path="/designer/notifications" element={<DesignerNotifications />} />
          <Route path="/designer/profile" element={<DesignerProfile />} />

          {/* Designer Portal Editor — same EditorPage, designer mode detected via path */}
          <Route path="/designer/create" element={<DesignerCreatePage />} />
          <Route path="/designer/editor" element={<EditorPage />} />
          <Route path="/designer/editor/:id" element={<EditorPage />} />

          {/* Admin Auth */}
          <Route path="/admin/login" element={<AdminLogin />} />

          {/* Admin — Core */}
          <Route path="/admin/dashboard" element={<AdminDashboard />} />

          {/* Admin — Content */}
          <Route path="/admin/templates" element={<AdminTemplates />} />
          {/* /admin/templates/import → the Template Engine import screen.
              Declared BEFORE the catch-all /:id. React Router ranks static
              segments above dynamic ones, so this would match either way —
              but relying on ranking here is invisible to the next reader. */}
          <Route path="/admin/templates/import" element={<AdminTemplateImport />} />
          {/* /admin/templates/:id/edit → full canvas editor (admin context).
              Declared BEFORE the catch-all /:id so it wins the match. */}
          <Route path="/admin/templates/:id/edit" element={<EditorPage />} />
          <Route path="/admin/templates/:id" element={<AdminTemplates />} />
          <Route path="/admin/ai-design-engine" element={<AdminAIDesignEngine />} />
          <Route path="/admin/ai-template-factory" element={<AdminAITemplateFactory />} />
          <Route path="/admin/ai-creative-director" element={<AdminAICreativeDirector />} />
          <Route path="/admin/ai-playground" element={<AdminAIPlayground />} />
          <Route path="/admin/ai-template-generator" element={<AITemplateGeneratorPage />} />
          <Route path="/admin/template-library" element={<AdminTemplateLibrary />} />
          <Route path="/admin/elements" element={<AdminElements />} />
          <Route path="/admin/elements/new" element={<AdminElements />} />
          <Route path="/admin/categories" element={<AdminCategories />} />
          <Route path="/admin/template-categories" element={<AdminTemplateCategories />} />
          <Route path="/admin/collections" element={<AdminCollections />} />

          {/* Admin — Blog */}
          <Route path="/admin/blog" element={<AdminBlog />} />
          <Route path="/admin/blog/:id" element={<AdminBlog />} />
          <Route path="/admin/blog-categories" element={<AdminBlogCategories />} />

          {/* Admin — Moderation */}
          <Route path="/admin/submissions" element={<AdminSubmissions />} />
          <Route path="/admin/ai-templates" element={<AdminAITemplates />} />
          <Route path="/admin/approvals" element={<AdminApprovals />} />

          {/* Admin — Media */}
          <Route path="/admin/assets" element={<AdminAssets />} />

          {/* Admin — Users */}
          <Route path="/admin/designers" element={<AdminDesigners />} />
          <Route path="/admin/users" element={<AdminUsers />} />

          {/* Admin — Platform */}
          <Route path="/admin/analytics" element={<AdminAnalytics />} />
          <Route path="/admin/settings" element={<AdminSettings />} />
          <Route path="/admin/generator" element={<AdminTemplateGenerator />} />
          <Route path="/admin/text-repair" element={<AdminTextRepair />} />
          <Route path="/admin/marketplace/payouts" element={<AdminMarketplacePayouts />} />
          <Route path="/admin/marketplace/creators" element={<AdminCreators />} />
          <Route path="/admin/marketplace/creators/:id" element={<AdminCreatorDetail />} />
          <Route path="/admin/marketplace/reports" element={<AdminReports />} />
          <Route path="/admin/contact-messages" element={<AdminContactMessages />} />

          {/* Anything the router does not know — never a blank page */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;
